// The top bar's switcher (spec/13 §2): where you are, and every place you can go instead — your venues, grouped under
// their organisation, each organisation's own area first where you may open it. It replaces a plain select that only
// listed venues and always opened the chosen one's Floor: choosing a place now opens the page you were on, at that
// place (`switch-route.ts`), and "All venues and organisations" at its foot opens the full page (`/venues`).
//
// The panel is the kit's Popover (Radix: it takes focus when it opens, returns it to the button, closes on Escape or a
// click outside) holding a listbox. With more than six places it opens on a search field — a combobox driving the
// listbox (WAI-ARIA's combobox-with-listbox, as the command palette); with fewer, the listbox takes focus itself, with
// the arrow keys, Home and End, type-ahead, and Enter or Space to go. The place you are at is ticked and
// `aria-current`. With one place and nothing else to open, the switcher is the place's name and no button.

import { useTranslation } from '@clavian/i18n/react';
import { cx, initialsOf, Popover } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { fetchMyAccess, type AccessScope, type MyOrganization, type MyVenue } from '../api/me';
import { queryKeys, useMyOrganizations, useMyVenues } from '../api/queries';
import type { PaletteAction } from './AppShell';
import { Icon, NavIcon, SearchIcon } from './nav-icons';
import type { ConsoleScope } from './scope';
import { ORG_GROUPS, VENUE_GROUPS, visibleGroups } from './sections';
import { usePlaceSwitch } from './place-switch';
import { switchRoute, type PlaceKind } from './switch-route';

/** More places than this and the panel opens on a search field. */
const SEARCH_ABOVE = 6;
/** How long a pause ends a type-ahead word, in milliseconds. */
const TYPE_AHEAD_MS = 700;

/**
 * Whether the switcher offers an organisation's own area (`/o/{slug}`): to an owner, or a member whose staff access
 * covers every venue — the rule the palette's settings search follows (spec/13 §2). A member of some venues only
 * reaches their venues; the organisation's screens they may read are linked from the venue.
 */
export function opensOrganization(organization: Pick<MyOrganization, 'allVenues'>): boolean {
  return organization.allVenues !== false;
}

/** The place the console is at: a venue, or an organisation's own area. */
export interface CurrentPlace {
  readonly kind: PlaceKind;
  readonly id: string;
}

export function currentPlaceOf(scope: ConsoleScope): CurrentPlace {
  return scope.kind === 'venue' ? { kind: 'venue', id: scope.venue.id } : { kind: 'organization', id: scope.organization.id };
}

/** One place in the switcher. */
export interface SwitcherEntry {
  /** Unique across the list: `venue:{id}` or `organization:{id}`. */
  readonly key: string;
  readonly kind: PlaceKind;
  readonly id: string;
  readonly slug: string;
  /** The venue's name, or the organisation's for its own area. */
  readonly name: string;
  /** The organisation the place belongs to, when it is known. */
  readonly organizationName: string | undefined;
  readonly current: boolean;
}

/** An organisation's places: its own area (where the member may open it), then its venues. */
export interface SwitcherGroup {
  readonly id: string;
  /** Absent for venues whose organisation is not among the member's (they are listed last, together). */
  readonly name: string | undefined;
  readonly venueCount: number;
  readonly entries: readonly SwitcherEntry[];
}

/**
 * The member's places, grouped by organisation in the order the server lists them (by name), each organisation's own
 * area first — where `opensOrganization` allows it, or where the member already is — then its venues. An organisation
 * that leaves nothing to list is left out.
 */
export function switcherGroups(
  organizations: readonly MyOrganization[],
  venues: readonly MyVenue[],
  current?: CurrentPlace,
): SwitcherGroup[] {
  const isCurrent = (kind: PlaceKind, id: string) => current !== undefined && current.kind === kind && current.id === id;
  const venueEntry = (venue: MyVenue, organizationName: string | undefined): SwitcherEntry => ({
    key: `venue:${venue.id}`,
    kind: 'venue',
    id: venue.id,
    slug: venue.slug,
    name: venue.name,
    organizationName,
    current: isCurrent('venue', venue.id),
  });
  const named = organizations.flatMap((organization): SwitcherGroup[] => {
    const own = venues.filter((venue) => venue.organizationId === organization.id);
    const area: SwitcherEntry[] =
      opensOrganization(organization) || isCurrent('organization', organization.id)
        ? [
            {
              key: `organization:${organization.id}`,
              kind: 'organization',
              id: organization.id,
              slug: organization.slug,
              name: organization.name,
              organizationName: organization.name,
              current: isCurrent('organization', organization.id),
            },
          ]
        : [];
    const entries = [...area, ...own.map((venue) => venueEntry(venue, organization.name))];
    return entries.length === 0 ? [] : [{ id: organization.id, name: organization.name, venueCount: own.length, entries }];
  });
  const unnamed = venues.filter((venue) => !organizations.some((organization) => organization.id === venue.organizationId));
  return unnamed.length === 0
    ? named
    : [...named, { id: 'other', name: undefined, venueCount: unnamed.length, entries: unnamed.map((venue) => venueEntry(venue, undefined)) }];
}

const entryCount = (groups: readonly SwitcherGroup[]): number => groups.reduce((sum, group) => sum + group.entries.length, 0);

function useSwitcherGroups(current: CurrentPlace | undefined): SwitcherGroup[] {
  const organizations = useMyOrganizations().data;
  const venues = useMyVenues().data;
  const kind = current?.kind;
  const id = current?.id;
  return useMemo(
    () => switcherGroups(organizations ?? [], venues ?? [], kind === undefined || id === undefined ? undefined : { kind, id }),
    [organizations, venues, kind, id],
  );
}

/** Whether the member may open a sidebar section at a place, by the sidebar's own rule (`sections.ts`). */
function sectionOpens(kind: PlaceKind, section: string, permissions: ReadonlySet<string>): boolean {
  const groups = visibleGroups(kind === 'venue' ? VENUE_GROUPS : ORG_GROUPS, { status: 'ready', can: (permission) => permissions.has(permission) }, { stock: true });
  return groups.some((group) => group.sections.some((candidate) => candidate.id === section));
}

/**
 * Go to a place, on the page you are on there (`switch-route.ts`). What the member may open there is read first — the
 * same answer the place's sidebar is drawn from, so it is ready when the page opens; if it cannot be read, the page is
 * opened anyway and asks the server itself.
 */
function useGoTo(): (entry: SwitcherEntry) => Promise<void> {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const place = usePlaceSwitch();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return useCallback(
    async (entry: SwitcherEntry) => {
      place.begin(entry.name);
      const scope: AccessScope = entry.kind === 'venue' ? { venueId: entry.id } : { organizationId: entry.id };
      const access = await queryClient
        .ensureQueryData({ queryKey: queryKeys.access(scope), queryFn: () => fetchMyAccess(api, scope), retry: false })
        .catch(() => undefined);
      const opens = access === undefined ? undefined : (section: string) => sectionOpens(entry.kind, section, access.permissions);
      await navigate(switchRoute(pathname, { kind: entry.kind, slug: entry.slug }, opens) as never);
    },
    [api, queryClient, navigate, pathname, place],
  );
}

/**
 * The switcher's open state, held by the area's layout so the palette's "Switch venue or organisation" opens the same
 * panel. The action is offered only where the switcher is a button: where there is somewhere else to go.
 */
export function useVenueSwitcher(current: CurrentPlace | undefined): {
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly action: PaletteAction | undefined;
} {
  const { t } = useTranslation('console');
  const [open, setOpen] = useState(false);
  const several = entryCount(useSwitcherGroups(current)) > 1;
  const action = useMemo<PaletteAction | undefined>(
    () =>
      several
        ? { id: 'switch-place', label: t('shell.switcher.title'), icon: <NavIcon name="venues" />, afterClose: true, run: () => setOpen(true) }
        : undefined,
    [several, t],
  );
  return { open, setOpen, action };
}

export interface VenueSwitcherProps {
  readonly scope: ConsoleScope;
  /** Controlled by the layout (`useVenueSwitcher`); uncontrolled without it. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

export function VenueSwitcher({ scope, open: controlled, onOpenChange }: VenueSwitcherProps): ReactNode {
  const { t } = useTranslation('console');
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlled ?? uncontrolled;
  const setOpen = (next: boolean) => {
    if (controlled === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  };
  const organizations = useMyOrganizations().data ?? [];
  const groups = useSwitcherGroups(currentPlaceOf(scope));
  const goTo = useGoTo();

  const name = scope.kind === 'venue' ? scope.venue.name : scope.organization.name;
  // The organisation's name under the venue's, when the member works for more than one; at the organisation, what it is.
  const detail =
    scope.kind === 'organization' ? t('shell.switcher.wholeOrganisation') : organizations.length > 1 ? scope.organization?.name : undefined;
  const face = (
    <>
      <span className="console-switcher__mark" aria-hidden="true">
        {initialsOf(name)}
      </span>
      <span className="console-switcher__text">
        <span className="console-switcher__name">{name}</span>
        {detail === undefined ? null : <span className="console-switcher__detail">{detail}</span>}
      </span>
    </>
  );

  if (entryCount(groups) <= 1) return <div className="console-switcher">{face}</div>;

  return (
    <Popover
      label={t('shell.switcher.title')}
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      align="start"
      className="console-switcher__panel"
      trigger={
        <button
          type="button"
          className="console-switcher console-switcher--button"
          aria-label={t('shell.switcher.trigger', { place: detail === undefined ? name : `${name}, ${detail}` })}
        >
          {face}
          <span className="console-switcher__chevron" aria-hidden="true">
            <Icon name="chevronDown" />
          </span>
        </button>
      }
    >
      <SwitcherPanel
        groups={groups}
        onChoose={(entry) => {
          setOpen(false);
          if (!entry.current) void goTo(entry);
        }}
        onLeave={() => setOpen(false)}
      />
    </Popover>
  );
}

interface IndexedEntry {
  readonly entry: SwitcherEntry;
  readonly index: number;
}

interface IndexedGroup {
  readonly group: SwitcherGroup;
  readonly entries: readonly IndexedEntry[];
}

function SwitcherPanel({
  groups,
  onChoose,
  onLeave,
}: {
  readonly groups: readonly SwitcherGroup[];
  readonly onChoose: (entry: SwitcherEntry) => void;
  readonly onLeave: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const listId = useId();
  const searchable = entryCount(groups) > SEARCH_ABOVE;
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: '', at: 0 });

  const labelOf = useCallback(
    (entry: SwitcherEntry) => (entry.kind === 'organization' ? t('shell.switcher.wholeOrganisation') : entry.name),
    [t],
  );

  // Filter by the query — a place's name, its organisation's, or "Whole organisation" — and number what is left in
  // order, without mutation, so a render never reassigns a variable after it completes.
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (entry: SwitcherEntry) =>
      needle === '' || [labelOf(entry), entry.name, entry.organizationName ?? ''].some((text) => text.toLowerCase().includes(needle));
    const kept = groups.map((group) => ({ group, entries: group.entries.filter(matches) })).filter((group) => group.entries.length > 0);
    const indexed = kept.reduce<{ readonly groups: readonly IndexedGroup[]; readonly total: number }>(
      (acc, { group, entries }) => ({
        groups: [...acc.groups, { group, entries: entries.map((entry, i) => ({ entry, index: acc.total + i })) }],
        total: acc.total + entries.length,
      }),
      { groups: [], total: 0 },
    );
    return { groups: indexed.groups, flat: kept.flatMap((group) => group.entries), count: indexed.total };
  }, [groups, query, labelOf]);

  // The place you are at is where the keys start, until a search narrows the list.
  const [active, setActive] = useState(() => Math.max(0, shown.flat.findIndex((entry) => entry.current)));
  const { count } = shown;
  const current = count === 0 ? -1 : ((active % count) + count) % count;
  const optionId = (index: number) => `${listId}-option-${index}`;
  const activeId = current < 0 ? undefined : optionId(current);

  // Focus where the keys work: the search field, or the list itself.
  useEffect(() => {
    (searchable ? inputRef.current : listRef.current)?.focus();
  }, [searchable]);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  /** Whether a type-ahead word is being typed: then Space is part of it ("Playground North"), not a choice. */
  const typing = () => typed.current.text !== '' && Date.now() - typed.current.at < TYPE_AHEAD_MS;

  const choose = (entry: SwitcherEntry | undefined) => {
    if (entry !== undefined) onChoose(entry);
  };

  /** Type-ahead: the next place whose name starts with what was typed; one letter again and again steps through them. */
  const typeAhead = (key: string) => {
    const now = Date.now();
    const text = (now - typed.current.at < TYPE_AHEAD_MS ? typed.current.text : '') + key.toLowerCase();
    typed.current = { text, at: now };
    const repeated = [...text].every((char) => char === text[0]);
    const needle = repeated ? key.toLowerCase() : text;
    const from = repeated ? current + 1 : current;
    for (let step = 0; step < count; step += 1) {
      const index = (((from + step) % count) + count) % count;
      const entry = shown.flat[index];
      if (entry !== undefined && labelOf(entry).toLowerCase().startsWith(needle)) {
        setActive(index);
        return;
      }
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>, list: boolean) => {
    if (count === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(current + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(current - 1 + count);
    } else if (event.key === 'Home' && list) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End' && list) {
      event.preventDefault();
      setActive(count - 1);
    } else if (event.key === 'Enter' || (list && event.key === ' ' && !typing())) {
      event.preventDefault();
      choose(shown.flat[current]);
    } else if (list && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      typeAhead(event.key);
    }
    // Escape is the popover's: it closes and returns focus to the switcher.
  };

  return (
    <div className="console-switcher__box">
      {searchable ? (
        <div className="console-switcher__search">
          <span className="console-switcher__search-icon" aria-hidden="true">
            <SearchIcon />
          </span>
          <input
            ref={inputRef}
            className="console-switcher__input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-label={t('shell.switcher.search')}
            placeholder={t('shell.switcher.search')}
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => onKeyDown(event, false)}
          />
        </div>
      ) : null}
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={t('shell.switcher.results')}
        className="console-switcher__list"
        {...(searchable ? {} : { tabIndex: 0, 'aria-activedescendant': activeId, onKeyDown: (event: KeyboardEvent<HTMLElement>) => onKeyDown(event, true) })}
      >
        {shown.groups.map(({ group, entries }) => (
          <div className="console-switcher__group" role="group" aria-label={group.name ?? t('shell.switcher.otherVenues')} key={group.id}>
            <div className="console-switcher__group-head" aria-hidden="true">
              <span className="console-switcher__group-name">{group.name ?? t('shell.switcher.otherVenues')}</span>
              <span className="console-switcher__group-count">{t('shell.switcher.venueCount', { count: group.venueCount })}</span>
            </div>
            {entries.map(({ entry, index }) => (
              // A button carries the option, as in the command palette: it is clickable by nature, and `tabIndex={-1}`
              // keeps focus on the field or the list, which drive it through `aria-activedescendant`.
              <button
                key={entry.key}
                type="button"
                id={optionId(index)}
                role="option"
                aria-selected={index === current}
                aria-current={entry.current ? 'true' : undefined}
                tabIndex={-1}
                data-active={index === current ? 'true' : undefined}
                className={cx('console-switcher__option', index === current && 'is-active', entry.current && 'is-current')}
                onClick={() => choose(entry)}
                onPointerMove={() => setActive(index)}
              >
                <span className={cx('console-switcher__option-mark', entry.kind === 'organization' && 'console-switcher__option-mark--organization')} aria-hidden="true">
                  {entry.kind === 'organization' ? <NavIcon name="organizations" /> : initialsOf(entry.name)}
                </span>
                <span className="console-switcher__option-label">{labelOf(entry)}</span>
                {entry.current ? (
                  <span className="console-switcher__option-check" aria-hidden="true">
                    <Icon name="check" />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ))}
        {count === 0 ? (
          // A non-actionable option keeps the listbox owning an option (valid ARIA) while it is empty.
          <div className="console-switcher__empty" role="option" aria-disabled="true" aria-selected={false}>
            {t('shell.switcher.empty', { query: query.trim() })}
          </div>
        ) : null}
      </div>
      <div className="console-switcher__foot">
        <Link to="/venues" className="console-switcher__all" onClick={onLeave}>
          <span>{t('shell.switcher.all')}</span>
          <Icon name="arrowRight" />
        </Link>
      </div>
    </div>
  );
}
